import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { setAppLanguage, type AppLanguage } from '../i18n';

/**
 * Language switch (T-15). The label is the endonym of the language it switches
 * to ("English" / "עברית"), marked with its own `lang` so screen readers read it
 * in the right voice. `<html lang dir>` is kept in step by i18n itself
 * (`i18n.on('languageChanged')` in src/i18n.ts), on every screen.
 */
/**
 * The language toggle as data, shared by this button (login, portal) and the
 * avatar menu / "עוד" sheet (M3): the target language, its endonym, the hint
 * (written in the target language) and the action.
 */
export function useLanguageToggle() {
  const { i18n, t } = useTranslation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const target: AppLanguage = i18n.language === 'en' ? 'he' : 'en';
  // The hint is written in the target language, so it is read in that voice.
  const hint = target === 'en' ? t('language.switchToEnglish') : t('language.switchToHebrew');
  const endonym = target === 'en' ? t('language.en') : t('language.he');

  const toggle = async () => {
    await setAppLanguage(target);

    if (isAuthenticated) {
      try {
        await api.updateLanguage(target);
      } catch {
        // Best-effort: the language already switched locally (and is remembered
        // on this device); only the server-side preference sync failed.
      }
    }
  };
  return { target, hint, endonym, toggle };
}

export default function LanguageSwitcher() {
  const { target, hint, endonym, toggle: toggleLanguage } = useLanguageToggle();

  return (
    <button
      type="button"
      onClick={toggleLanguage}
      lang={target}
      aria-label={hint}
      title={hint}
      className="touch-target flex items-center gap-1.5 px-2 py-1.5 text-sm text-surface-500 hover:bg-surface-100 hover:text-surface-700 rounded-lg transition-colors"
    >
      <Globe size={16} strokeWidth={1.75} aria-hidden="true" />
      <span className="hidden md:inline text-xs font-medium">
        {endonym}
      </span>
    </button>
  );
}
